import React, { useEffect, useRef, useState, useCallback } from "react";
import "../App.css";
import {mobile} from "../services/mobileBridge";

const W = 400;
const H = 600;
const GRAVITY = 0.45;
const FLAP = -7.5;
const PIPE_W = 60;
const GAP = 160;
const PIPE_SPEED = 2.6;
const PIPE_INTERVAL = 1500;
const BIRD_R = 14;
const BIRD_X = 100;

type Pipe = { x: number; gapY: number; passed: boolean };
type Particle = { x: number; y: number; vx: number; vy: number; life: number; color: string };

function SkyHopper({ country }: { country: 'south-africa' | 'nigeria' }) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [score, setScore] = useState(0);
    const [highScore, setHighScore] = useState(() => Number(localStorage.getItem("skyHopperHi") || 0));
    const [started, setStarted] = useState(false);
    const [gameOver, setGameOver] = useState(false);

    const stateRef = useRef({
        birdY: H / 2,
        birdV: 0,
        birdRot: 0,
        pipes: [] as Pipe[],
        particles: [] as Particle[],
        lastPipe: 0,
        score: 0,
        dead: false,
        started: false,
        bgOffset: 0,
    });

    const animRef = useRef<number>(0);
    const lastTimeRef = useRef<number>(0);

    const flap = useCallback(() => {
        const s = stateRef.current;
        if (!s.started) {
            s.started = true;
            setStarted(true);
        }
        if (s.dead) return;
        s.birdV = FLAP;
        for (let i = 0; i < 6; i++) {
            s.particles.push({
                x: BIRD_X - 8,
                y: s.birdY,
                vx: -Math.random() * 2 - 1,
                vy: (Math.random() - 0.5) * 2,
                life: 1,
                color: `hsl(${180 + Math.random() * 60}, 80%, 70%)`,
            });
        }
    }, []);

    const reset = useCallback(() => {
        const s = stateRef.current;
        s.birdY = H / 2;
        s.birdV = 0;
        s.birdRot = 0;
        s.pipes = [];
        s.particles = [];
        s.lastPipe = 0;
        s.score = 0;
        s.dead = false;
        s.started = false;
        setScore(0);
        setGameOver(false);
        setStarted(false);
        if (animRef.current) cancelAnimationFrame(animRef.current);
        lastTimeRef.current = 0;
        animRef.current = requestAnimationFrame(loop);
    }, []);

    const loop = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d", { alpha: false });
        if (!ctx) return;
        const s = stateRef.current;
        const now = performance.now();
        const dt = lastTimeRef.current ? Math.min((now - lastTimeRef.current) / 16.67, 2) : 1;
        lastTimeRef.current = now;

        if (s.started && !s.dead) {
            s.birdV += GRAVITY * dt;
            s.birdY += s.birdV * dt;
            s.birdRot = Math.max(-30, Math.min(90, s.birdV * 5));

            s.bgOffset = (s.bgOffset + 0.5 * dt) % W;

            if (s.birdY > H - 20 || s.birdY < 10) {
                s.dead = true;
                mobile.haptic("gameover");
                setGameOver(true);
                setScore(s.score);
                if (s.score > highScore) {
                    setHighScore(s.score);
                    localStorage.setItem("skyHopperHi", String(s.score));
                }
            }

            if (now - s.lastPipe > PIPE_INTERVAL) {
                s.pipes.push({ x: W, gapY: 120 + Math.random() * (H - 240 - GAP), passed: false });
                s.lastPipe = now;
            }

            for (const p of s.pipes) {
                p.x -= PIPE_SPEED * dt;
                if (!p.passed && p.x + PIPE_W < BIRD_X) {
                    p.passed = true;
                    s.score++;
                    setScore(s.score);
                }
                const bx = BIRD_X;
                const by = s.birdY;
                if (bx + BIRD_R > p.x && bx - BIRD_R < p.x + PIPE_W) {
                    if (by - BIRD_R < p.gapY || by + BIRD_R > p.gapY + GAP) {
                        s.dead = true;
                        mobile.haptic("gameover");
                        setGameOver(true);
                        setScore(s.score);
                        if (s.score > highScore) {
                            setHighScore(s.score);
                            localStorage.setItem("skyHopperHi", String(s.score));
                        }
                    }
                }
            }
            s.pipes = s.pipes.filter(p => p.x > -PIPE_W);
        }

        s.particles = s.particles.filter(pt => pt.life > 0);
        for (const pt of s.particles) {
            pt.x += pt.vx * dt;
            pt.y += pt.vy * dt;
            pt.life -= 0.02 * dt;
        }

        // Draw
        const sky = ctx.createLinearGradient(0, 0, 0, H);
        sky.addColorStop(0, "#1a1a2e");
        sky.addColorStop(0.4, "#16213e");
        sky.addColorStop(1, "#0f3460");
        ctx.fillStyle = sky;
        ctx.fillRect(0, 0, W, H);

        // Stars
        ctx.fillStyle = "rgba(255,255,255,0.15)";
        for (let i = 0; i < 40; i++) {
            const sx = (i * 73 - s.bgOffset * 0.3) % W;
            const sy = (i * 97) % H;
            const sr = (i % 3) + 1;
            ctx.fillRect((sx + W) % W, sy, sr, sr);
        }

        // Pipes
        for (const p of s.pipes) {
            const grad = ctx.createLinearGradient(p.x, 0, p.x + PIPE_W, 0);
            grad.addColorStop(0, "#2dd4bf");
            grad.addColorStop(0.5, "#14b8a6");
            grad.addColorStop(1, "#0d9488");
            ctx.fillStyle = grad;
            ctx.fillRect(p.x, 0, PIPE_W, p.gapY);
            ctx.fillRect(p.x, p.gapY + GAP, PIPE_W, H - p.gapY - GAP);
            ctx.fillStyle = "rgba(255,255,255,0.15)";
            ctx.fillRect(p.x, p.gapY - 4, PIPE_W, 4);
            ctx.fillRect(p.x, p.gapY + GAP, PIPE_W, 4);
        }

        // Particles
        for (const pt of s.particles) {
            ctx.globalAlpha = pt.life;
            ctx.fillStyle = pt.color;
            ctx.beginPath();
            ctx.arc(pt.x, pt.y, 3, 0, Math.PI * 2);
            ctx.fill();
        }
        ctx.globalAlpha = 1;

        // Bird
        ctx.save();
        ctx.translate(BIRD_X, s.birdY);
        ctx.rotate((s.birdRot * Math.PI) / 180);
        const bg = ctx.createRadialGradient(-4, -4, 0, 0, 0, BIRD_R);
        bg.addColorStop(0, "#fde047");
        bg.addColorStop(0.5, "#facc15");
        bg.addColorStop(1, "#eab308");
        ctx.fillStyle = bg;
        ctx.beginPath();
        ctx.arc(0, 0, BIRD_R, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = "rgba(255,255,255,0.5)";
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.fillStyle = "#1a1a2e";
        ctx.beginPath();
        ctx.arc(6, -4, 3, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#f97316";
        ctx.beginPath();
        ctx.moveTo(BIRD_R - 2, 0);
        ctx.lineTo(BIRD_R + 8, -2);
        ctx.lineTo(BIRD_R + 8, 2);
        ctx.closePath();
        ctx.fill();
        ctx.restore();

        // Ground
        ctx.fillStyle = "#1a1a2e";
        ctx.fillRect(0, H - 12, W, 12);
        ctx.fillStyle = "rgba(45, 212, 191, 0.3)";
        for (let i = 0; i < W; i += 20) {
            ctx.fillRect(((i - s.bgOffset) % W + W) % W, H - 12, 10, 12);
        }

        animRef.current = requestAnimationFrame(loop);
    }, [highScore]);

    useEffect(() => {
        animRef.current = requestAnimationFrame(loop);
        return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
    }, [loop]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.code === "Space" || e.code === "ArrowUp") {
                e.preventDefault();
                flap();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [flap]);

    const handleCanvasClick = (e: React.MouseEvent | React.TouchEvent) => {
        e.preventDefault();
        flap();
    };

    return (
        <div className="container">
            <a href={`/game-studio/${country}`} className="game-back-link">&larr; Back to Games</a>
            <div className="score-display">Score: <span>{score}</span> &nbsp; Best: <span>{highScore}</span></div>
            <div style={{ position: "relative", display: "inline-block", borderRadius: 16, overflow: "hidden" }}>
                <canvas
                    ref={canvasRef}
                    width={W}
                    height={H}
                    className="game"
                    style={{ width: "100%", maxWidth: W, touchAction: "none" }}
                    onClick={handleCanvasClick}
                    onTouchStart={handleCanvasClick}
                />
                {!started && (
                    <div className="overlay">
                        <h2>🐦 Sky Hopper</h2>
                        <p style={{ color: "var(--text-2)", fontSize: "0.9rem" }}>Tap or press Space to fly</p>
                        <button className="center-btn" onClick={flap}>▶ Start</button>
                    </div>
                )}
                {gameOver && (
                    <div className="overlay">
                        <h2>💥 Score: {score}</h2>
                        {score >= highScore && score > 0 && <p style={{ color: "var(--good)" }}>New Best!</p>}
                        <button className="center-btn" onClick={reset}>🔄 Play Again</button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default SkyHopper;
